import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL + "/api";

const normalizeArrayPayload = (payload, candidateKeys = []) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    for (const key of candidateKeys) {
        if (Array.isArray(payload?.[key])) {
            return payload[key];
        }
    }

    return [];
};

axios.defaults.headers.post["Content-Type"] = "application/json";

/* VANHENTUNUT KIRJAUTUMISPOLETTI TYHJENSI ETUSIVUN (Mika 24.10.2026).
   Oire: etusivulla luki "Myytävät kohteet (243)" mutta listan tilalla oli
   "Valitettavasti haku ei löytänyt yhtään kohdetta" — sama kaikissa kolmessa
   listassa (myytävät, huutokaupat, vuokrattavat).
   Syy: selaimen localStorageen jäänyt vanhentunut JWT lähti mukaan myös
   julkisissa hauissa (getAuctions lisää Authorization-otsakkeen aina, jos
   poletti löytyy). Symfonyn jwt-autentikaattori hylkää kelvottoman poletin
   401:llä jo ennen reittiä, vaikka ^/api on PUBLIC_ACCESS — eli pyyntö
   kaatui, vaikka sama haku ilman otsaketta palauttaa 200 ja 201 kohdetta.
   AuthContext siivoaa poletin vasta checkSessionin jälkeen, eikä lista
   hae uudelleen, joten etusivu jäi tyhjäksi koko käynnin ajaksi.
   Korjaus kahdessa kerroksessa, koska kumpikin yksin jättäisi aukon:
   1) vanhentunutta polettia ei lähetetä lainkaan (exp luetaan itse),
   2) jos palvelin silti vastaa 401:n, poletti unohdetaan ja pyyntö
      uusitaan kerran ilman sitä (esim. kelvoton tai mitätöity poletti). */
const poletinVanhentunut = (poletti) => {
    try {
        const hyotykuorma = String(poletti).split(".")[1];
        if (!hyotykuorma) return false;
        const purettu = atob(hyotykuorma.replace(/-/g, "+").replace(/_/g, "/"));
        const exp = JSON.parse(purettu).exp;
        return typeof exp === "number" && exp * 1000 <= Date.now();
    } catch {
        return false;
    }
};

const poletinOtsakkeesta = (config) => {
    const otsake = config?.headers?.Authorization ?? config?.headers?.authorization;
    return typeof otsake === "string" && otsake.startsWith("Bearer ") ? otsake.slice(7).trim() : null;
};

const unohdaPoletti = (config) => {
    try {
        localStorage.removeItem("token");
    } catch {
        /* yksityinen selaustila voi estää kirjoituksen */
    }
    if (config?.headers) {
        delete config.headers.Authorization;
        delete config.headers.authorization;
    }
};

axios.interceptors.request.use((config) => {
    const poletti = poletinOtsakkeesta(config);
    if (poletti && poletinVanhentunut(poletti)) {
        unohdaPoletti(config);
    }
    return config;
});

axios.interceptors.response.use(
    (vastaus) => vastaus,
    (virhe) => {
        const config = virhe?.config;
        if (virhe?.response?.status === 401 && config && !config.__uusittuIlmanPolettia && poletinOtsakkeesta(config)) {
            config.__uusittuIlmanPolettia = true;
            unohdaPoletti(config);
            return axios(config);
        }
        return Promise.reject(virhe);
    },
);

const api = {
    saveUser: async (userData) => {
        const token = localStorage.getItem("token");

        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const response = await axios.post(`${API_URL}/user/save`, userData, { headers });

            return response.data;
        } catch (error) {
            console.error("Error saving user:", error);
            throw error;
        }
    },

    confirmUser: async (token) => {
        try {
            const response = await axios.get(`${API_URL}/user/confirm/${token}`);

            return response.data;
        } catch (error) {
            console.error("Error confirming user:", error);
            throw error;
        }
    },

    getBadges: async () => {
        const token = localStorage.getItem("token");

        if (!token) {
            return { auctions: 0, favorites: 0 };
        }

        try {
            const response = await axios.get(`${API_URL}/user/badges`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
            });

            const data = response.data;
            // Normalize and validate
            const auctions = Number(data?.auctions ?? 0);
            const favorites = Number(data?.favorites ?? 0);
            return {
                auctions: Number.isFinite(auctions) ? auctions : 0,
                favorites: Number.isFinite(favorites) ? favorites : 0,
            };
        } catch (error) {
            console.error("Error fetching badge count:", error);
            // Do not throw to avoid bubbling errors into UI render loops
            return { auctions: 0, favorites: 0 };
        }
    },

    login: async (userData) => {
        try {
            const response = await axios.post(`${API_URL}/login_check`, userData);

            return response.data;
        } catch (error) {
            console.error("Error logging in:", error);
            throw error;
        }
    },

    getAuctionBids: async (number) => {
        const token = localStorage.getItem("token");

        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const response = await axios.get(`${API_URL}/auction/${number}/bids`, {
                headers,
            });

            return response.data;
        } catch (error) {
            console.error(`Error fetching bids for auction with number ${number}:`, error);
            throw error;
        }
    },

    getAuctionOffers: async (number) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/auction/offers`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error(`Error fetching offers for auction with number ${number}:`, error);
            throw error;
        }
    },

    acceptOffer: async (offerId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/auction/offer/${offerId}/accept`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error(`Error approving offer with ID ${offerId}:`, error);
            throw error;
        }
    },

    rejectOffer: async (offerId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/auction/offer/${offerId}/reject`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error(`Error rejecting offer with ID ${offerId}:`, error);
            throw error;
        }
    },

    cancelOwnOffer: async (offerId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/user/auction/offer/${offerId}/cancel`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            return response.data;
        } catch (error) {
            console.error(`Error cancelling offer with ID ${offerId}:`, error);
            throw error;
        }
    },

    archiveOffer: async (offerId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/user/auction/offer/${offerId}/archive`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            return response.data;
        } catch (error) {
            console.error(`Error archiving offer with ID ${offerId}:`, error);
            throw error;
        }
    },

    getUser: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching user:", error);
            throw error;
        }
    },

    checkSession: async () => {
        try {
            const token = localStorage.getItem("token");

            const response = await axios.get(`${API_URL}/session`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error checking session:", error);
            throw error;
        }
    },

    createAuction: async (propertyData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/user/auction/create`, propertyData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error creating property:", error);
            throw error;
        }
    },

    getUserAuction: async (auctionUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/auction/${auctionUuid}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error(`Error fetching auction with ID ${auctionUuid}:`, error);
            throw error;
        }
    },

    deleteAuction: async (auctionUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.delete(`${API_URL}/user/auction/${auctionUuid}/delete`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error(`Error deleting auction with ID ${auctionUuid}:`, error);
            throw error;
        }
    },

    getAuction: async (number) => {
        try {
            const token = localStorage.getItem("token");
            const headers = {};

            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            const response = await axios.get(`${API_URL}/auction/${number}`, { headers });

            return response.data;
        } catch (error) {
            console.error(`Error fetching auction with ID ${number}:`, error);
            throw error;
        }
    },

    getAuctions: async ({ filters, limit = 0, page = 1 }) => {
        try {
            const token = localStorage.getItem("token");
            const headers = {};

            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            const response = await axios.get(`${API_URL}/auctions?page=${page}&limit=${limit}&filters=${JSON.stringify(filters)}`, {
                headers,
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching auctions:", error);
            throw error;
        }
    },

    getUserAuctions: async (status = '', limit = 0, userId = 0) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/auctions`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: { status, limit, userId },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching auctions:", error);
            throw error;
        }
    },

    getAdminAuctions: async (status, type, source, searchTerm, page, pageSize, userId = null, sortField = null, sortOrder = null) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/auctions`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: { status, type, source, searchTerm, page, pageSize, userId, sortField, sortOrder },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching auctions:", error);
            throw error;
        }
    },

    saveAuction: async (auctionUuid, auctionData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/user/auction/${auctionUuid}/save`, auctionData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error(`Error saving auction with ID ${auctionUuid}:`, error);
            throw error;
        }
    },

    publishAuction: async (auctionUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/auction/${auctionUuid}/publish`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error(`Error publishing auction with ID ${auctionUuid}:`, error);
            throw error;
        }
    },

    uploadFile: async (file, params) => {
        const token = localStorage.getItem("token");
        const formData = new FormData();

        formData.append("file", file);
        formData.append("uuid", params.auctionId || "");
        formData.append("folderId", params.folderId || "");
        formData.append("propertyId", params.propertyId || "");
        formData.append("category", params.category || "");

        try {
            const response = await axios.post(`${API_URL}/user/files/upload`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error uploading file:", error);
            throw error;
        }
    },

    deleteFile: async (fileId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.delete(`${API_URL}/user/files/delete/${fileId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error deleting file:", error);
            throw error;
        }
    },

    getUserImages: async (folderId, page, pageSize) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(
                `${API_URL}/user/images?folder=${folderId}&page=${page}&pageSize=${pageSize}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error fetching images:", error);
            throw error;
        }
    },

    getGalleryFolders: async (folderId = null) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/gallery/folders`, {
                params: { folderId },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching folders:", error);
            throw error;
        }
    },

    deleteImage: async (id) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.delete(`${API_URL}/user/image/delete/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error deleting image:", error);
            throw error;
        }
    },

    deleteFolder: async (id) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.delete(`${API_URL}/user/files/folder/delete/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error deleting folder:", error);
            throw error;
        }
    },

    createFolder: async (name, parentId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/user/files/folder/create`,
                { name, parentId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error creating folder:", error);
            throw error;
        }
    },

    /* KOKOKANSIOT (Mika 14.9.2026): kohteen julkisen kuvapankin oikean
       huoneistokoon kuvat ilmoituslomakkeelle. */
    haePankkikuvatKohteelle: async (propertyId, area) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/property-bank-images`, {
                params: { propertyId, area },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching bank images:", error);
            throw error;
        }
    },

    getFolder: async (folderId = null, page, pageSize) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/files/folder`, {
                params: { folderId, page, pageSize },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching folder:", error);
            throw error;
        }
    },

    getProperties: async () => {
        try {
            const response = await axios.get(`${API_URL}/properties`);

            return response.data;
        } catch (error) {
            console.error("Error fetching properties:", error);
            throw error;
        }
    },

    getUserProperties: async (page, pageSize, searchTerm, area) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/properties`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: { page, pageSize, searchTerm, area },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching properties:", error);
            throw error;
        }
    },

    getUserProperty: async (propertyId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/property/${propertyId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error(`Error fetching property with ID ${propertyId}:`, error);
            throw error;
        }
    },

    getProperty: async (propertyId) => {
        try {
            const response = await axios.get(`${API_URL}/property/${propertyId}`);

            return response.data;
        } catch (error) {
            console.error(`Error fetching property with ID ${propertyId}:`, error);
            throw error;
        }
    },

    getAvailableAreas: async () => {
        try {
            const response = await axios.get(`${API_URL}/areas`);

            return normalizeArrayPayload(response.data, ["areas", "items", "data"]);
        } catch (error) {
            console.error("Error fetching areas:", error);
            throw error;
        }
    },

    getAvailableAreasAuctions: async () => {
        try {
            const response = await axios.get(`${API_URL}/areas-auctions`);

            return normalizeArrayPayload(response.data, ["areas", "items", "data"]);
        } catch (error) {
            console.error("Error fetching areas:", error);
            throw error;
        }
    },

    getPropertyImages: async (propertyId, page, pageSize) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/property/${propertyId}/images`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: { page, pageSize },
            });

            return response.data;
        } catch (error) {
            console.error(`Error fetching images for property with ID ${propertyId}:`, error);
            throw error;
        }
    },

    createUserProperty: async (propertyData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/user/property/create`, propertyData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error creating property:", error);
            throw error;
        }
    },

    saveUserProperty: async (propertyId, propertyData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/user/property/${propertyId}/save`, propertyData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error(`Error saving property with ID ${propertyId}:`, error);
            throw error;
        }
    },

    submitBid: async (auctionId, bidData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/auction/${auctionId}/bid`, bidData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error submitting bid:", error);
            throw error;
        }
    },

    submitOffer: async (auctionId, offerData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/auction/${auctionId}/offer`, offerData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error submitting offer:", error);
            throw error;
        }
    },

    saveAuctionReminder: async (auctionNumber, enabled) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/auction/${auctionNumber}/reminder`,
                { enabled },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error saving auction reminder:", error);
            throw error;
        }
    },

    sendChatMessage: async (messageData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/chat/message/send`, messageData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error sending message:", error);
            throw error;
        }
    },

    getUserChats: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/chats`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching chats:", error);
            throw error;
        }
    },

    getChatMessages: async (chatUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/chat/${chatUuid}/messages`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching messages:", error);
            throw error;
        }
    },

    getSettings: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/settings`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching settings:", error);
            throw error;
        }
    },

    // Tietoturva 3.11.2026 (K1): kauppakirja ladataan lyhytikäisellä allekirjoitetulla
    // linkillä, jonka saa vain kirjautunut myyjä tai ylläpitäjä.
    haeSopimuksenLatauslinkki: async (uuid) => {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/user/auction/${uuid}/contract/lataa-linkki`, {
            headers: { Authorization: `Bearer ${token}` },
        });

        return response.data.url;
    },

    getSetting: async (settingName) => {
        try {
            const response = await axios.get(`${API_URL}/settings/${settingName}`);

            return response.data;
        } catch (error) {
            console.error("Error fetching setting:", error);
            throw error;
        }
    },

    getSettingById: async (settingId) => {
        // Tietoturva 3.11.2026: PHC-asetukset (alennuskoodit) vain ylläpitäjälle -> token mukaan
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/settings/id/${settingId}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching setting:", error);
            throw error;
        }
    },

    saveSetting: async (settingId, settingData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.put(`${API_URL}/admin/settings/update/${settingId}`, settingData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error saving setting:", error);
            throw error;
        }
    },

    deleteSetting: async (settingId) => {
        const token = localStorage.getItem("token");

        try {
            await axios.delete(`${API_URL}/admin/settings/delete/${settingId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch (error) {
            console.error("Error deleting setting:", error);
            throw error;
        }
    },

    createSetting: async (settingData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/admin/settings/create`, settingData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error creating setting:", error);
            throw error;
        }
    },

    // Page Management API methods
    getPages: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/pages`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching pages:", error);
            throw error;
        }
    },

    getPageById: async (pageId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/pages/${pageId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching page:", error);
            throw error;
        }
    },

    createPage: async (pageData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/admin/pages`, pageData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error creating page:", error);
            throw error;
        }
    },

    updatePage: async (pageId, pageData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.put(`${API_URL}/admin/pages/${pageId}`, pageData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error updating page:", error);
            throw error;
        }
    },

    deletePage: async (pageId) => {
        const token = localStorage.getItem("token");

        try {
            await axios.delete(`${API_URL}/admin/pages/${pageId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch (error) {
            console.error("Error deleting page:", error);
            throw error;
        }
    },

    savePageContent: async (pageId, contentData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/admin/pages/${pageId}/content`, contentData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error saving page content:", error);
            throw error;
        }
    },

    getPublicPage: async (slug, locale = 'fi') => {
        try {
            const response = await axios.get(`${API_URL}/pages/${slug}`, {
                params: { locale },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching public page:", error);
            throw error;
        }
    },

    createResaleOrder: async (payload) => {
        const token = localStorage.getItem("token");

        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.post(`${API_URL}/resale-order/create`, payload, { headers });

            return response.data;
        } catch (error) {
            console.error("Error creating resale order:", error);
            throw error;
        }
    },

    getCurrentUserTimeshares: async () => {
        const token = localStorage.getItem("token");

        if (!token) {
            return [];
        }

        try {
            const response = await axios.get(`${API_URL}/user/timeshares`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
            });

            return normalizeArrayPayload(response.data);
        } catch (error) {
            console.error("Error fetching current user timeshares:", error);
            throw error;
        }
    },

    getResaleOrderPaymentStatus: async (stamp) => {
        try {
            const response = await axios.get(`${API_URL}/resale-order/payment/${stamp}`);

            return response.data;
        } catch (error) {
            console.error("Error fetching resale payment status:", error);
            throw error;
        }
    },

    setAuctionExpired: async (auctionUuid) => {
        try {
            const response = await axios.get(`${API_URL}/auction/${auctionUuid}/expire`);

            return response.data;
        } catch (error) {
            console.error("Error expiring auction:", error);
            throw error;
        }
    },

    getApartments: async (propertyId) => {
        try {
            const response = await axios.get(`${API_URL}/property/${propertyId}/apartments`);

            return response.data;
        } catch (error) {
            console.error("Error fetching apartments:", error);
            throw error;
        }
    },

    updateApartment: async (apartmentId, apartmentData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/user/apartment/${apartmentId}/save`, apartmentData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error(`Error saving apartment with ID ${apartmentId}:`, error);
            throw error;
        }
    },

    deleteApartment: async (apartmentId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.delete(`${API_URL}/user/apartment/${apartmentId}/delete`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error(`Error deleting apartment with ID ${apartmentId}:`, error);
            throw error;
        }
    },

    addApartment: async (propertyId, newApartment) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/user/apartment/create`,
                { ...newApartment, propertyId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error adding apartment:", error);
            throw error;
        }
    },

    getCities: async () => {
        try {
            const response = await axios.get(`${API_URL}/cities`);

            return response.data;
        } catch (error) {
            console.error("Error fetching cities:", error);
            throw error;
        }
    },

    getFilesSummary: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/god/files/summary`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching file summary:", error);
            throw error;
        }
    },

    generateMissingFiles: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/god/files/generateMissing`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error generating missing files:", error);
            throw error;
        }
    },

    getUsers: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/users`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching users:", error);
            throw error;
        }
    },

    getRegenerationProgress: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/god/files/regenerationProgress`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching progress:", error);
            throw error;
        }
    },

    regenerateImages: async (sizes) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/god/files/regenerateImages`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    sizes: sizes.join(","), // Pass sizes as a comma-separated string
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error regenerating images:", error);
            throw error;
        }
    },

    toggleFavorite: async (auctionNumber) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/auction/${auctionNumber}/favorite`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error toggling favorite:", error);
            throw error;
        }
    },

    getFavoriteAuctions: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/favorites`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching favorite auctions:", error);
            throw error;
        }
    },

    toggleFeatured: async (auctionId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/superadmin/auction/${auctionId}/toggleFeatured`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error toggling featured:", error);
            throw error;
        }
    },

    getFeaturedAuctions: async () => {
        try {
            const response = await axios.get(`${API_URL}/auctions/featured`);

            return response.data;
        } catch (error) {
            console.error("Error fetching featured auctions:", error);
            throw error;
        }
    },

    sendContactMessage: async (messageData) => {
        try {
            const response = await axios.post(`${API_URL}/sendContactMessage`, messageData);

            return response.data;
        } catch (error) {
            console.error("Error sending contact message:", error);
            throw error;
        }
    },

    exchangeAuthCodeForToken: async (code, state) => {
        try {
            const response = await axios.post(`${API_URL}/auth/exchange`, { code, state });

            return response.data;
        } catch (error) {
            console.error("Error exchanging auth code for token:", error);
            throw error;
        }
    },

    decodeIdToken: async (token) => {
        try {
            const response = await axios.post(`${API_URL}/auth/decode`, { token });

            return response.data;
        } catch (error) {
            console.error("Error decoding token:", error);
            throw error;
        }
    },

    getAuthData: async (authorizationCode) => {
        try {
            const response = await axios.post(`${API_URL}/auth/data`, { authorizationCode });

            return response.data;
        } catch (error) {
            console.error("Error fetching auth data:", error);
            throw error;
        }
    },

    getUserInfo: async (accessToken) => {
        try {
            const response = await axios.post(`${API_URL}/auth/userinfo`, { accessToken });

            return response.data;
        } catch (error) {
            console.error("Error fetching user info:", error);
            throw error;
        }
    },

    getContract: async (auctionUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/auction/${auctionUuid}/contract`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching contract:", error);
            throw error;
        }
    },

    saveContract: async (auctionUuid, contractData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/user/auction/${auctionUuid}/contract/save`, contractData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error saving contract:", error);
            throw error;
        }
    },

    requestPasswordReset: async (email) => {
        try {
            const response = await axios.post(`${API_URL}/password/reset`, { email });

            return response.data;
        } catch (error) {
            console.error("Error requesting password reset:", error);
            throw error;
        }
    },

    resetPassword: async (data) => {
        try {
            const response = await axios.post(`${API_URL}/password/reset/confirm`, data);

            return response.data;
        } catch (error) {
            console.error("Error resetting password:", error);
            throw error;
        }
    },

    changePassword: async (data) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/user/password/change`, data, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error changing password:", error);
            throw error;
        }
    },

    fetchPrice: async (auctionUuid, discountCode) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/user/auction/price`,
                { auctionUuid, discountCode },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error fetching prices:", error);
            throw error;
        }
    },

    /* Ulkopuolisen kaupan kauppakirjapohja (11.8.2026) */
    createUlkopuolinenContract: async (payload) => {
        const token = localStorage.getItem("token");
        const response = await axios.post(`${API_URL}/user/contracts/ulkopuolinen`, payload, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    createPayment: async (payload) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/payment/create`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error creating payment:", error);
            throw error;
        }
    },

    confirmPayment: async (stamp, transactionId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/payment/confirm`,
                { stamp, transactionId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error confirming payment:", error);
            throw error;
        }
    },

    createFeaturePayment: async (auctionUuid, months) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/featured/payment/create`,
                { auctionUuid, months },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error creating payment:", error);
            throw error;
        }
    },

    confirmFeaturePayment: async (transactionId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/featured/payment/confirm`,
                { transactionId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error confirming payment:", error);
            throw error;
        }
    },

    getDiscountCodes: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/discount-codes`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching discount codes:", error);
            throw error;
        }
    },

    saveDiscountCodes: async (discountCodes) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/discount-codes/save`, discountCodes, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error saving discount codes:", error);
            throw error;
        }
    },

    deleteDiscountCode: async (discountCodeId) => {
        const token = localStorage.getItem("token");

        try {
            await axios.delete(`${API_URL}/discount-codes/delete/${discountCodeId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch (error) {
            console.error("Error deleting discount code:", error);
            throw error;
        }
    },

    getUnreadMessagesCount: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/chat/unreadMessagesCount`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching unread messages count:", error);
            throw error;
        }
    },

    sendEmail: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/sendEmail`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error sending email:", error);
            throw error;
        }
    },

    getContactMessages: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/contactMessages`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching contact messages:", error);
            throw error;
        }
    },

    createOrder: async (orderData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/user/order/create`, orderData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error creating order:", error);
            throw error;
        }
    },

    createOrderPayment: async (orderUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/payment/order/create`,
                { orderUuid },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error creating order payment:", error);
            throw error;
        }
    },

    userAction: async (userId, action) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/admin/user/action`,
                { userId, action },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error toggling email confirmed:", error);
            throw error;
        }
    },

    transferAuctionToUser: async (auctionUuid, userId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/admin/auction/transfer`,
                { auctionUuid, userId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error transferring auction:", error);
            throw error;
        }
    },

    sendContactRequest: async (auctionUuid) => {
        const token = localStorage.getItem("token");

        const data = {
            'auctionUuid': auctionUuid,
        };

        try {
            const response = await axios.post(`${API_URL}/auction/contactRequest`, data, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error sending contact request:", error);
            throw error;
        }
    },

    getProducts: async (category) => {
        try {
            const response = await axios.get(`${API_URL}/products`, {
                params: { category },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching products:", error);
            throw error;
        }
    },

    getProduct: async (code) => {
        try {
            const response = await axios.get(`${API_URL}/products/${code}`);

            return response.data;
        } catch (error) {
            console.error("Error fetching product:", error);
            throw error;
        }
    },

    saveProducts: async (products) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/admin/products/save`, products, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error saving products:", error);
            throw error;
        }
    },

    getProductPrice: async (productCode) => {
        try {
            const response = await axios.get(`${API_URL}/products/price`, {
                params: { productCode },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching product price:", error);
            throw error;
        }
    },

    updateFeaturedUntil: async (auctionUuid, date) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/admin/auction/updateFeaturedUntil`,
                { auctionUuid, date },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error updating featured date:", error);
            throw error;
        }
    },

    getBlogPosts: async () => {
        try {
            const response = await axios.get(`${API_URL}/posts`);

            return response.data;
        } catch (error) {
            console.error("Error fetching blog posts:", error);
            throw error;
        }
    },

    getAdminBlogPosts: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/posts`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching admin blog posts:", error);
            throw error;
        }
    },

    getBlogPost: async (postId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/posts/${postId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching blog post:", error);
            throw error;
        }
    },

    deleteBlogPost: async (postId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.delete(`${API_URL}/admin/posts/${postId}/delete`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error deleting blog post:", error);
            throw error;
        }
    },

    updateBlogPost: async (postId, postData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.put(
                `${API_URL}/admin/posts/${postId}/update`,
                postData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error updating blog post:", error);
            throw error;
        }
    },

    getBlogPostBySlug: async (slug) => {
        try {
            const response = await axios.get(`${API_URL}/posts/slug/${slug}`);

            return response.data;
        } catch (error) {
            console.error("Error fetching blog post by slug:", error);
            throw error;
        }
    },

    haeTilastot: async () => {
        try {
            const response = await axios.get(`${API_URL}/tilastot`);

            return response.data;
        } catch (error) {
            console.error("Error fetching site stats:", error);
            throw error;
        }
    },

    auctionsCountByType: async () => {
        try {
            const response = await axios.get(`${API_URL}/auction/countByType/`);

            return response.data;
        } catch (error) {
            console.error("Error counting auctions:", error);
            throw error;
        }
    },

    uploadBlogImage: async (formData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/admin/posts/fileUpload`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error uploading blog file:", error);
            throw error;
        }
    },

    confirmAuctionPayment: async (auctionUuid, paymentAmount) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/auction/confirmPayment`,
                { auctionUuid, paymentAmount },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error confirming auction payment:", error);
            throw error;
        }
    },

    /* TEHO1-LASKUT (Aurora 19.8.2026). Sahkopostilla lahetetyt Teho1-laskut
       eivat nay Maksusuorituksissa, koska niilla ei ole Paytrail-maksurivia.
       Lahde on laskurekisteri, jonka maksutilanteen paivittaa Holvin
       paivaraportista teho1-maksuvahti.js. */
    /* Luonnostilit (Mika 30.8.2026): ilmoituksen luonti ilman rekisteroitymista.
       aloitaLuonnos on ainoa kutsu ilman tokenia - se LUO istunnon. */
    /* lahde (Mika 3.11.2026): myyntikehotteen kautta luodut tilit erottuvat API:n
       nginx-lokissa (?lahde=kehote). Taustapalvelu ei lue parametria. */
    aloitaLuonnos: async (lahde) => {
        const response = await axios.post(`${API_URL}/luonnos/aloita` + (lahde ? `?lahde=${encodeURIComponent(lahde)}` : ""), {});
        return response.data;
    },

    aktivoiLuonnos: async (email, password, confirm) => {
        const token = localStorage.getItem("token");
        const response = await axios.post(
            `${API_URL}/user/luonnos/aktivoi`,
            { email, password, confirm },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    },

    /* Kevyt sähköpostin jättö kesken lomaketta (Mika 9.9.2026) — ei
       salasanaa, ei tiliä, vain ohjeet/vinkit sähköpostiin. */
    jataVarhainenEmail: async (email) => {
        const token = localStorage.getItem("token");
        const response = await axios.post(
            `${API_URL}/luonnos/vinkit`,
            { email },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    },

    getAdminLuonnokset: async () => {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/admin/luonnokset`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    getAdminTeho1Invoices: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/teho1-invoices`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching Teho1 invoices:", error);
            throw error;
        }
    },

    downloadTeho1Invoice: async (numero) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/teho1-invoice/${numero}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: "blob",
            });

            return response.data;
        } catch (error) {
            console.error("Error downloading Teho1 invoice:", error);
            throw error;
        }
    },

    getAdminCommissionInvoices: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/commission-invoices`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching commission invoices:", error);
            throw error;
        }
    },

    downloadCommissionInvoice: async (auctionUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/auction/${auctionUuid}/commission-invoice`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: "blob",
            });

            return response.data;
        } catch (error) {
            console.error("Error downloading commission invoice:", error);
            throw error;
        }
    },

    downloadBatchCommissionInvoices: async (auctionUuids) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/admin/commission-invoices/batch`,
                { auctionUuids },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    responseType: "blob",
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error downloading batch commission invoices:", error);
            throw error;
        }
    },

    getOffers: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/offers`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching offers:", error);
            throw error;
        }
    },

    getAllBids: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/bids`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching bids:", error);
            throw error;
        }
    },

    getUserById: async (id) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/user/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching user:", error);
            throw error;
        }
    },

    getAreas: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/areas`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return normalizeArrayPayload(response.data, ["areas", "items", "data"]);
        } catch (error) {
            console.error("Error fetching areas:", error);
            throw error;
        }
    },

    deleteArea: async (id) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.delete(`${API_URL}/admin/areas/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error deleting area:", error);
            throw error;
        }
    },

    getArea: async (id) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/areas/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching area:", error);
            throw error;
        }
    },

    updateArea: async (id, data) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.put(
                `${API_URL}/admin/areas/${id}`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error updating area:", error);
            throw error;
        }
    },

    createArea: async (data) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/admin/areas`, data, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error creating area:", error);
            throw error;
        }
    },

    getAreaBySlug: async (slug) => {
        try {
            const response = await axios.get(`${API_URL}/areas/${slug}`);

            return response.data;
        } catch (error) {
            console.error("Error fetching area by slug:", error);
            throw error;
        }
    },

    getPayments: async (userId = null) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/payments`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: userId ? { user_id: userId } : {},
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching payments:", error);
            throw error;
        }
    },

    downloadPaymentReceipt: async (paymentId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/payments/${paymentId}/receipt`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: "blob",
            });

            return response.data;
        } catch (error) {
            console.error("Error downloading payment receipt:", error);
            throw error;
        }
    },

    downloadBatchPaymentReceipts: async (paymentIds) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/admin/payments/receipts/batch`,
                { paymentIds },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    responseType: "blob",
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error downloading batch payment receipts:", error);
            throw error;
        }
    },

    /* Puhelinmyynti (Mika 13.9.2026) */
    getPuhelinLista: async (haku, tila, kaikki) => {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/puhelin/lista`, {
            params: { haku: haku || "", tila: tila || "", kaikki: kaikki ? 1 : "" },
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    getPuhelinLiidi: async (id) => {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/puhelin/liidi/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    getPuhelinSeuraava: async (ohita) => {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/puhelin/seuraava`, {
            params: { ohita: ohita || 0 },
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    postPuhelinTapahtuma: async (payload) => {
        const token = localStorage.getItem("token");
        const response = await axios.post(`${API_URL}/puhelin/tapahtuma`, payload, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    postPuhelinKauppalinkki: async (liidiId, alennus) => {
        const token = localStorage.getItem("token");
        const response = await axios.post(
            `${API_URL}/puhelin/kauppalinkki`,
            { liidiId, alennus },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    },

    postPuhelinYhteystiedot: async (liidiId, email) => {
        const token = localStorage.getItem("token");
        const response = await axios.post(
            `${API_URL}/puhelin/yhteystiedot`,
            { liidiId, email },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    },

    postPuhelinLahetaEsite: async (liidiId, kampanja = false) => {
        const token = localStorage.getItem("token");
        const response = await axios.post(
            `${API_URL}/puhelin/laheta-esite`,
            { liidiId, kampanja },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    },

    getPuhelinOpas: async () => {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/puhelin/opas`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    getAdminPuhelinmyynti: async () => {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/admin/puhelinmyynti`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    getResaleOrders: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/resale-orders`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching resale orders:", error);
            throw error;
        }
    },

    updateResaleOrderStatus: async (orderId, payload) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/admin/resale-orders/${orderId}/status`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error updating resale order status:", error);
            throw error;
        }
    },

    saveSearchAlert: async (alertData) => {
        try {
            const response = await axios.post(`${API_URL}/search-alerts/save`, alertData);

            return response.data;
        } catch (error) {
            console.error("Error saving search alert:", error);
            throw error;
        }
    },

    getSearchAlerts: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/search-alerts`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching search alerts:", error);
            throw error;
        }
    },

    deleteSearchAlert: async (alertUuid) => {
        try {
            const response = await axios.delete(`${API_URL}/search-alerts/${alertUuid}/delete`);

            return response.data;
        } catch (error) {
            console.error("Error deleting search alert:", error);
            throw error;
        }
    },

    confirmSearchAlert: async (alertUuid) => {
        try {
            const response = await axios.post(`${API_URL}/search-alerts/${alertUuid}/confirm`);

            return response.data;
        } catch (error) {
            console.error("Error confirming search alert:", error);
            throw error;
        }
    },

    getSearchAlert: async (alertUuid) => {
        try {
            const response = await axios.get(`${API_URL}/search-alerts/${alertUuid}`);

            return response.data;
        } catch (error) {
            console.error("Error fetching search alert:", error);
            throw error;
        }
    },

    renewSearchAlert: async (alertUuid) => {
        try {
            const response = await axios.post(`${API_URL}/search-alerts/${alertUuid}/renew`);

            return response.data;
        } catch (error) {
            console.error("Error renewing search alert:", error);
            throw error;
        }
    },

    copyAuction: async (auctionUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/user/auction/copy/${auctionUuid}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error copying auction:", error);
            throw error;
        }
    },

    // Customer notes
    getCustomerNotes: async (userId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/user/${userId}/notes`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching customer notes:", error);
            throw error;
        }
    },

    createCustomerNote: async (userId, noteData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(`${API_URL}/admin/user/${userId}/notes`, noteData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error creating customer note:", error);
            throw error;
        }
    },

    updateCustomerNote: async (userId, noteId, noteData) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.put(`${API_URL}/admin/user/${userId}/notes/${noteId}`, noteData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error updating customer note:", error);
            throw error;
        }
    },

    deleteCustomerNote: async (userId, noteId) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.delete(`${API_URL}/admin/user/${userId}/notes/${noteId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error deleting customer note:", error);
            throw error;
        }
    },

    downloadContract: async (auctionUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/user/auction/${auctionUuid}/contract/download`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: 'blob', // Important for file download
            });

            return response.data;
        } catch (error) {
            console.error("Error downloading contract:", error);
            throw error;
        }
    },

    getContracts: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/contracts`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching contracts:", error);
            throw error;
        }
    },

    sendContractEmail: async (auctionUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/admin/contract/${auctionUuid}/send-email`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error sending contract email:", error);
            throw error;
        }
    },

    getContractSigningStatus: async (auctionUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/contract/${auctionUuid}/signing-status`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching contract signing status:", error);
            throw error;
        }
    },

    syncContractSigningStatus: async (auctionUuid) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.post(
                `${API_URL}/admin/contract/${auctionUuid}/sync-signing-status`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error syncing contract signing status:", error);
            throw error;
        }
    },

    getLeads: async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(`${API_URL}/admin/leads`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching leads:", error);
            throw error;
        }
    },

    updateLead: async (leadId, payload) => {
        const token = localStorage.getItem("token");

        try {
            const response = await axios.put(`${API_URL}/admin/leads/${leadId}`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error updating lead:", error);
            throw error;
        }
    }
};

export default api;
